import { useEffect, useState } from 'preact/hooks';
import type { Manifest } from '../../shared/manifest.ts';
import { checkRom, RomTooBig, type RomCheck } from '../../shared/rom.ts';
import { deleteRom, idbAvailable, listRoms, putRom, romBytes, type StoredRom } from '../lib/db.ts';
import { formatBytes, PLATFORM_NAMES } from '../format.ts';

export interface ChosenRom { bytes: Uint8Array; sha1: string; name: string }

type State =
  | { s: 'idle' }
  | { s: 'reading'; name: string }
  | { s: 'wrong'; name: string; check: RomCheck }
  | { s: 'error'; message: string };

export function RomScreen({ manifest, onRom }: { manifest: Manifest; onRom: (rom: ChosenRom) => void }) {
  const [state, setState] = useState<State>({ s: 'idle' });
  const [remember, setRemember] = useState(false);
  const [over, setOver] = useState(false);
  const [stored, setStored] = useState<StoredRom[]>([]);
  const canStore = idbAvailable();

  const refresh = () => { if (canStore) listRoms(manifest.platform).then(setStored, () => setStored([])); };
  useEffect(refresh, []);

  const accept = async (name: string, check: RomCheck, fromStore = false) => {
    if (remember && !fromStore && canStore) {
      await putRom({ sha1: check.sha1, name, platform: manifest.platform, bytes: check.bytes }).catch(() => {});
    }
    onRom({ bytes: check.bytes, sha1: check.sha1, name });
  };

  const handle = async (name: string, bytes: Uint8Array, fromStore = false) => {
    setState({ s: 'reading', name });
    try {
      const check = await checkRom(bytes, name, manifest);
      if (check.matches === false) setState({ s: 'wrong', name, check });
      else await accept(name, check, fromStore);
    } catch (e) {
      setState({ s: 'error', message: e instanceof RomTooBig ? `That file is too big: ${e.message}.` : `Could not read that file (${String(e)}).` });
    }
  };

  const onFile = async (f: File | undefined | null) => {
    if (!f) return;
    await handle(f.name, new Uint8Array(await f.arrayBuffer()));
  };

  const matching = stored.filter((r) => !manifest.rom.sha1?.length || manifest.rom.sha1.includes(r.sha1));
  const others = stored.filter((r) => !matching.includes(r));

  return (
    <section class="rom" data-state="rom">
      <h2>Load ROM</h2>
      <p>
        Choose your own {PLATFORM_NAMES[manifest.platform] ?? manifest.platform} ROM ({manifest.rom.accept.join(', ')}).
        It is read on this device only and passed only to the game's sandbox.
      </p>

      {matching.length > 0 && (
        <ul class="stored" data-field="stored-roms">
          {matching.map((r) => (
            <li key={r.sha1}>
              <button class="primary" data-action="use-stored" onClick={async () => handle(r.name, await romBytes(r), true)}>
                Use {r.name}
              </button>{' '}
              <span class="muted">{formatBytes(r.size)} · remembered on this device</span>{' '}
              <button data-action="forget" onClick={() => deleteRom(r.sha1).then(refresh)}>Forget</button>
            </li>
          ))}
        </ul>
      )}

      <div class={'drop' + (over ? ' over' : '')} data-field="drop"
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); onFile(e.dataTransfer?.files[0]); }}>
        <p>Drop the ROM file here, or</p>
        <label class="button">
          <input type="file" data-field="rom" accept={manifest.rom.accept.join(',')}
            onChange={(e) => { const i = e.target as HTMLInputElement; onFile(i.files?.[0]); i.value = ''; }} />
        </label>
      </div>

      {canStore && (
        <label class="check">
          <input type="checkbox" data-field="remember" checked={remember} onChange={(e) => setRemember((e.target as HTMLInputElement).checked)} />
          {' '}Remember this ROM on this device
        </label>
      )}

      {state.s === 'reading' && <p class="muted">Checking {state.name}…</p>}
      {state.s === 'error' && <p class="field-error" role="alert">{state.message}</p>}
      {state.s === 'wrong' && (
        <div class="warning" role="alert" data-state="wrong-rom">
          <p><strong>This isn't the ROM this build expects.</strong> The game may not start or may behave strangely.</p>
          <details>
            <summary>Details</summary>
            <pre>{`${state.name}\nSHA-1 ${state.check.sha1}\nexpected ${manifest.rom.sha1!.join('\n      or ')}${state.check.notes.length ? '\n' + state.check.notes.join('\n') : ''}`}</pre>
          </details>
          <button data-action="continue-anyway" onClick={() => accept(state.name, state.check)}>Continue anyway</button>{' '}
          <button onClick={() => setState({ s: 'idle' })}>Choose another file</button>
        </div>
      )}

      {others.length > 0 && (
        <details class="muted">
          <summary>Other remembered {PLATFORM_NAMES[manifest.platform] ?? ''} ROMs</summary>
          <ul>
            {others.map((r) => (
              <li key={r.sha1}>
                <button onClick={async () => handle(r.name, await romBytes(r), true)}>Use {r.name}</button>{' '}
                <button onClick={() => deleteRom(r.sha1).then(refresh)}>Forget</button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
